import type { MeResponse } from '@/js/account/shared/model';
import axios from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { accountMeShow } from '@/js/account/entities/session/api/me.api';
import { refreshAccountCsrfToken } from '@/js/account/shared/api';

export const useAuthStore = defineStore('account-auth', () => {
  const user = ref<MeResponse | null>(null);
  const needsEmailVerification = ref(false);
  const bootstrapped = ref(false);
  const loading = ref(false);
  let bootstrapPromise: Promise<void> | null = null;
  let sessionProbeGeneration = 0;

  function invalidateSessionProbes(): void {
    sessionProbeGeneration += 1;
  }

  function isStaleSessionProbe(generation: number): boolean {
    return generation !== sessionProbeGeneration;
  }

  async function fetchMe(): Promise<MeResponse | null> {
    const generation = sessionProbeGeneration;
    loading.value = true;
    needsEmailVerification.value = false;

    try {
      const me = await accountMeShow();

      if (isStaleSessionProbe(generation)) {
        return user.value;
      }

      user.value = me;
      return me;
    }
    catch (error) {
      if (isStaleSessionProbe(generation)) {
        return user.value;
      }

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;

        if (status === 401) {
          user.value = null;
          return null;
        }

        if (status === 403) {
          needsEmailVerification.value = true;
          return null;
        }
      }

      if (import.meta.env.DEV) {
        console.error('[account] GET /api/account/me failed', error);
      }
      user.value = null;
      return null;
    }
    finally {
      if (!isStaleSessionProbe(generation)) {
        loading.value = false;
      }
    }
  }

  async function bootstrap(): Promise<void> {
    if (bootstrapped.value) {
      return;
    }

    if (bootstrapPromise !== null) {
      return bootstrapPromise;
    }

    bootstrapPromise = (async () => {
      try {
        await refreshAccountCsrfToken();
        await fetchMe();
      }
      finally {
        bootstrapped.value = true;
        bootstrapPromise = null;
      }
    })();

    return bootstrapPromise;
  }

  function clearUser(): void {
    user.value = null;
    needsEmailVerification.value = false;
  }

  return {
    user,
    needsEmailVerification,
    bootstrapped,
    loading,
    fetchMe,
    bootstrap,
    invalidateSessionProbes,
    clearUser,
  };
});
